import { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { LifeBuoy, Paperclip, X, Loader2, FileText, ArrowUpRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const CONTRACTING_ISSUES = [
  { id: 'STATUS_INQUIRY', label: 'Contracting status inquiry' },
  { id: 'CARRIER_TRANSFER', label: 'Carrier transfer information/documentation' },
  { id: 'OTHER', label: 'Other contracting related issue/inquiry' },
];

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const AttachmentPicker = ({ attachments, onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files.length) return;
    if (attachments.length + files.length > 5) return toast.error('Up to 5 attachments per ticket');
    setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/api/tickets/attachments`, fd);
        added.push(res.data);
      }
      onChange([...attachments, ...added]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid="ticket-attachments">
      <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5 text-[#2447D6]" /> Attach documents <span className="font-normal text-[#A3AED0]">(optional · PDF, Word or image · max 20MB)</span></Label>
      <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="ticket-attachment-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#E9EDF7] bg-[#F4F7FE] py-3 text-xs font-semibold text-[#707EAE] transition-colors hover:border-[#2447D6]/40 hover:text-[#2447D6] disabled:opacity-60" data-testid="ticket-attachment-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}{busy ? 'Uploading…' : 'Click to choose files (e.g. transfer form, release form)'}
      </button>
      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-2 rounded-lg border border-[#E9EDF7] bg-white px-3 py-2 text-xs" data-testid={`ticket-attachment-${a.id}`}>
              <FileText className="h-3.5 w-3.5 shrink-0 text-[#2447D6]" />
              <span className="min-w-0 flex-1 truncate font-medium text-[#2B3674]">{a.filename}</span>
              <span className="text-[#A3AED0]">{fmtSize(a.size)}</span>
              <button type="button" onClick={() => onChange(attachments.filter((x) => x.id !== a.id))} className="rounded-full p-1 text-[#A3AED0] hover:bg-red-50 hover:text-red-500" aria-label="Remove attachment"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AttachmentList = ({ attachments }) => {
  if (!attachments?.length) return null;
  return (
    <div>
      <div className="mb-1 text-xs uppercase text-[#A3AED0]">Attachments</div>
      <ul className="space-y-1.5">
        {attachments.map((a) => (
          <li key={a.id}>
            <a href={`${API}/api/tickets/attachments/${a.id}/file`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg border border-[#E9EDF7] bg-white px-3 py-2 text-xs font-medium text-[#2447D6] hover:bg-[#F4F7FE]" data-testid={`ticket-attachment-link-${a.id}`}>
              <FileText className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 flex-1 truncate">{a.filename}</span><span className="text-[#A3AED0]">{fmtSize(a.size)}</span><ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ContractingFields = ({ value, onChange, carriers, reasonText, setReasonText }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const issue = value.contracting_issue;
  return (
    <>
      <div className="space-y-1">
        <Label>Contracting issue *</Label>
        <select value={issue || ''} onChange={(e) => set({ contracting_issue: e.target.value, carrier_id: '', writing_number: '' })} required className="h-10 w-full rounded-md border border-[#E9EDF7] bg-white px-3" data-testid="contracting-issue-select">
          <option value="">Select the type of issue</option>
          {CONTRACTING_ISSUES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>

      {issue === 'STATUS_INQUIRY' && (
        <div className="flex gap-3 rounded-xl border border-[#D89E2F]/40 bg-[#FFF8E7] p-4" data-testid="contracting-status-notice">
          <LifeBuoy className="mt-0.5 h-5 w-5 shrink-0 text-[#B47E14]" />
          <div className="text-xs leading-relaxed text-[#6B4E0B]">
            <p className="font-bold">Check Support &amp; Questions first</p>
            <p className="mt-1">Most contracting status questions (timelines, what "pending" means, carrier-specific steps) are already answered in the <Link to="/support" className="font-bold underline underline-offset-2">Support &amp; Questions</Link> section of ATLAS. Please review it before continuing — if your question isn't covered, describe the issue in detail below.</p>
          </div>
        </div>
      )}

      {issue === 'CARRIER_TRANSFER' && (
        <>
          <div className="rounded-xl bg-[#F4F7FE] p-3 text-xs leading-relaxed text-[#2B3674]" data-testid="contracting-transfer-notice">
            For agents eligible to transfer an existing carrier contract from another IMO to Breeze. Select the carrier, enter your current writing number with that carrier in the comments, and attach any transfer or release form you have.
          </div>
          <div className="flex gap-3 rounded-xl border border-[#D89E2F]/40 bg-[#FFF8E7] p-3" data-testid="contracting-surelc-reminder">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#B47E14]" />
            <p className="text-xs leading-relaxed text-[#6B4E0B]"><span className="font-bold">Reminder:</span> make sure you have also requested a <span className="font-bold">"Transfer"</span> for this carrier in <span className="font-bold">SureLC</span>. The transfer cannot be processed until that request is submitted there.</p>
          </div>
          <div className="space-y-1">
            <Label>Carrier you're eligible to transfer *</Label>
            <select value={value.carrier_id || ''} onChange={(e) => set({ carrier_id: e.target.value })} required className="h-10 w-full rounded-md border border-[#E9EDF7] bg-white px-3" data-testid="contracting-carrier-select">
              <option value="">Select carrier</option>
              {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Current writing number with this carrier *</Label>
            <Input value={value.writing_number || ''} onChange={(e) => set({ writing_number: e.target.value })} required placeholder="e.g. 1234567" data-testid="contracting-writing-number" />
          </div>
        </>
      )}

      {issue && (
        <div className="space-y-1">
          <Label>{issue === 'CARRIER_TRANSFER' ? 'Comments (include your writing number and any details) *' : 'Describe the issue in detail *'}</Label>
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} required rows={4} placeholder={issue === 'STATUS_INQUIRY' ? 'Which carrier(s), when you submitted contracting, and what you have heard so far…' : issue === 'CARRIER_TRANSFER' ? 'Current IMO, writing number, and anything the contracting team should know…' : 'Tell us what you need help with…'} data-testid="reason-text" />
        </div>
      )}

      {(issue === 'CARRIER_TRANSFER' || issue === 'OTHER') && <AttachmentPicker attachments={value.attachments || []} onChange={(attachments) => set({ attachments })} />}
    </>
  );
};
